import type { LabelColor } from '@/lib/api/generated/model';

/**
 * What each of the API's eight colour slots looks like.
 *
 * Each slot now has its own tint. This is the change D1 was shaped to make
 * cheap: the backend stores a *slot* and never a colour (spec 063 FR-006), so
 * appearance stays on this side of the seam, and giving the slots distinct
 * appearances turned out to be this file plus sixteen lines of `globals.css`,
 * touching no component — which is what FR-013 required of whoever did it.
 *
 * The eight values are authored, not brand values, and they ship under a
 * recorded waiver of the brandbook rule against new colour ways (see
 * `docs/brand.md`, "Waived deviations", 2026-08-27). Two facts drove that:
 * every one of the eight brand secondaries is already bound to a board *state*
 * role, and their hue angles cluster into four families. A chip cannot wear the
 * board's word for what a task is doing, and two indistinguishable pairs would
 * not have been eight slots.
 *
 * What has *not* changed is the part that matters more than colour:
 *
 * - The chip is still a filled pill containing its name, never a rail and never
 *   a dot — the forms the board reserves for state (FR-010).
 * - The name is still always present, so colour is never the only thing telling
 *   two labels apart (FR-011). Someone who cannot distinguish these hues loses
 *   nothing they had before; everyone else gains scanning.
 * - The text is still `--label-chip-foreground` on every slot rather than a
 *   colour derived from the slot (FR-012), which is why the tints are built at
 *   one fixed lightness: every chip carries the same contrast, and there is no
 *   slot that happens to be the illegible one.
 */
const LABEL_SLOT_CLASS: Record<LabelColor, string> = {
  slot_1: 'bg-label-slot-1 text-label-chip-foreground',
  slot_2: 'bg-label-slot-2 text-label-chip-foreground',
  slot_3: 'bg-label-slot-3 text-label-chip-foreground',
  slot_4: 'bg-label-slot-4 text-label-chip-foreground',
  slot_5: 'bg-label-slot-5 text-label-chip-foreground',
  slot_6: 'bg-label-slot-6 text-label-chip-foreground',
  slot_7: 'bg-label-slot-7 text-label-chip-foreground',
  slot_8: 'bg-label-slot-8 text-label-chip-foreground',
};

/**
 * The chip classes for a slot. An unrecognised slot — a backend that grew a
 * ninth before this file learned about it — falls back to the neutral chip
 * role rather than to one of the eight, because a ninth label silently wearing
 * the first one's colour is worse than a ninth label wearing none: the first is
 * a wrong answer, the second is visibly no answer.
 */
export function labelSlotClass(color: LabelColor): string {
  return LABEL_SLOT_CLASS[color] ?? 'bg-label-chip text-label-chip-foreground';
}

/** The eight slots the API stores, in their order. */
export const LABEL_SLOTS: readonly LabelColor[] = [
  'slot_1',
  'slot_2',
  'slot_3',
  'slot_4',
  'slot_5',
  'slot_6',
  'slot_7',
  'slot_8',
];

/**
 * The slot a new label should take (spec 228): the least-used one, ties by slot
 * order — the same rule the backend applies when no colour is sent, so the
 * create dialog can show the choice before it is made rather than after.
 */
export function nextLabelSlot(
  registry: readonly { color: LabelColor }[],
): LabelColor {
  const used = new Map<LabelColor, number>();
  for (const label of registry) {
    used.set(label.color, (used.get(label.color) ?? 0) + 1);
  }
  let best: LabelColor = LABEL_SLOTS[0] ?? 'slot_1';
  let fewest = Number.POSITIVE_INFINITY;
  for (const slot of LABEL_SLOTS) {
    const count = used.get(slot) ?? 0;
    if (count < fewest) {
      best = slot;
      fewest = count;
    }
  }
  return best;
}
